import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Newspaper, Calendar, CheckCircle2, EyeOff, Filter, X } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import DataTable from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import Thumbnail from '../common/Thumbnail';
import { Modal, FormField, FileUpload, PrimaryButton, SecondaryButton } from '../common/FormComponents';
import { newsAPI, categoriesAPI } from '../../services/api';
import { NEWS_STATUSES, COLORS, formatLabel } from '../../utils/constants';
import { newsImage } from '../../utils/media';

const C = COLORS;

const NewsPage = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, today: 0, active: 0, inactive: 0 });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const [categories, setCategories] = useState([]);

  // Fetch news articles with pagination, category, search, and status
  const fetchData = useCallback(async () => {
    try {
      const params = {
        page,
        limit: itemsPerPage,
        ...filter,
      };
      if (selectedCategory) {
        params.category_id = selectedCategory;
      }
      const res = await newsAPI.getAll(params);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error(err);
    }
  }, [page, itemsPerPage, filter, selectedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch counts: total news, today's news, total active, total inactive
  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (selectedCategory) {
        params.category_id = selectedCategory;
      }
      const res = await newsAPI.getStats(params);
      setStats(res.data || { total: 0, today: 0, active: 0, inactive: 0 });
    } catch (err) {
      console.error('Failed to fetch news stats:', err);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Load category options for the many-to-one dropdown
  useEffect(() => {
    categoriesAPI.getAll({ limit: 1000 })
      .then(res => setCategories(res.data.data || []))
      .catch(err => console.error(err));
  }, []);

  const categoryOptions = categories.map(c => ({ value: c.id, label: c.name }));

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  // Remove the stored cover image. Saved immediately (not on form submit) so
  // the list and the mobile app reflect it right away; the modal just drops
  // the preview.
  const handleRemoveCover = async () => {
    if (!modal?.id) return;
    if (!window.confirm('Remove this cover image? The file is deleted from the server.')) return;
    setLoading(true);
    try {
      await newsAPI.removeCover(modal.id);
      setForm(p => ({ ...p, _image: null }));
      fetchData();
      fetchStats();
    } catch (err) {
      console.error(err);
      window.alert(err?.response?.data?.error || 'Could not remove the image.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && !k.startsWith('_') && fd.append(k, v));
      if (files[0]) fd.append('cover_image', files[0]);

      if (modal?.id) {
        await newsAPI.update(modal.id, fd);
      } else {
        await newsAPI.create(fd);
      }
      setModal(null);
      setForm({});
      setFiles([]);
      fetchData();
      fetchStats();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await newsAPI.delete(row.id);
      fetchData();
      fetchStats();
    } catch (err) {
      alert('Failed to delete article');
    }
  };

  const openEdit = async (row) => {
    setModal({ id: row.id });
    setFiles([]);
    // The list omits the heavy `body`, so fetch the full article for editing
    try {
      const res = await newsAPI.getById(row.id);
      const r = res.data;
      setForm({ title: r.title, description: r.description, body: r.body, category_id: r.category_id || '', status: r.status, _image: newsImage(r) });
    } catch (err) {
      setForm({ title: row.title, description: row.description, category_id: row.category_id || '', status: row.status, _image: newsImage(row) });
    }
  };

  const columns = [
    { key: 'cover_image', label: 'Image', render: (v, row) => <Thumbnail src={newsImage(row)} alt={row.title} /> },
    { key: 'title', label: 'Title', render: v => <span style={{ fontWeight: 600, color: C.g800 }}>{v}</span> },
    { key: 'category_name', label: 'Category', render: v => v ? <span style={{ fontSize: 12, color: C.g500, background: C.g100, padding: '3px 10px', borderRadius: 6 }}>{v}</span> : <span style={{ fontSize: 12, color: C.g400 }}>—</span> },
    { key: 'author_name', label: 'Author / Source', render: (v, r) => v || (r.source_name
      ? <span style={{ fontSize: 12, color: C.g500 }}>{r.source_name}{r.source ? ` · ${r.source}` : ''}</span>
      : <span style={{ color: C.g400 }}>—</span>) },
    { key: 'created_at', label: 'Date', render: v => new Date(v).toLocaleDateString() },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  // Selected category object for display
  const activeCategoryObj = categories.find(c => String(c.id) === String(selectedCategory));

  return (
    <AppLayout title="News Management">
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.g900 }}>News Articles</h2>
          <span style={{ fontSize: 13, color: C.g500, marginTop: 2, display: 'block' }}>
            Manage news feeds, categories, article content, and publication status
          </span>
        </div>
        <PrimaryButton onClick={() => { setForm({}); setFiles([]); setModal({}); }} icon={<Plus size={18} />}>
          Add Article
        </PrimaryButton>
      </div>

      {/* ─── 2 Metric Stat Boxes (Total News & Today News | Total Active & Total Inactive) ─── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 16,
        marginBottom: 20
      }}>
        {/* Box 1: Total News and Today's News */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          border: `1.5px solid #C7D2FE`,
          padding: '20px 22px',
          boxShadow: '0 4px 14px rgba(79, 70, 229, 0.05), 0 1px 3px rgba(0,0,0,0.02)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #4F46E5 0%, #818CF8 100%)'
          }} />

          {/* Box Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                News Ingestion Overview
              </span>
              <h3 style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 800, color: C.g900 }}>
                Total News & Today
              </h3>
            </div>
            <div style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: '#EEF2FF',
              color: '#4F46E5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Newspaper size={20} />
            </div>
          </div>

          {/* Dual Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'flex-start' }}>
            {/* Total News Count */}
            <div style={{ borderRight: `1px solid ${C.g100}`, paddingRight: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.g500, display: 'block' }}>
                Total News
              </span>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.g900, marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {stats.total?.toLocaleString() ?? 0}
              </div>
              <span style={{ fontSize: 11, color: C.g400, marginTop: 4, display: 'block' }}>
                {activeCategoryObj ? `In ${activeCategoryObj.name}` : 'All articles in bank'}
              </span>
            </div>

            {/* Today News Count */}
            <div style={{ paddingLeft: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.g500 }}>
                  Today's News
                </span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#15803D',
                  background: '#DCFCE7',
                  padding: '2px 6px',
                  borderRadius: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3
                }}>
                  <Calendar size={10} /> Today
                </span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#16A34A', marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                +{stats.today?.toLocaleString() ?? 0}
              </div>
              <span style={{ fontSize: 11, color: C.g400, marginTop: 4, display: 'block' }}>
                Published today
              </span>
            </div>
          </div>

          {/* Subtitle / Category Note */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 12,
            borderTop: `1px solid ${C.g100}`,
            marginTop: 14
          }}>
            <span style={{ fontSize: 12, color: C.g600, fontWeight: 500 }}>
              {activeCategoryObj ? `Category filter: ${activeCategoryObj.name}` : 'Complete multi-source repository'}
            </span>
            {selectedCategory && (
              <button
                type="button"
                onClick={() => { setSelectedCategory(''); setPage(1); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#4F46E5',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: 0
                }}
              >
                Reset <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Box 2: Total Active and Total Inactive */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          border: `1.5px solid #A7F3D0`,
          padding: '20px 22px',
          boxShadow: '0 4px 14px rgba(16, 185, 129, 0.05), 0 1px 3px rgba(0,0,0,0.02)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)'
          }} />

          {/* Box Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#059669', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Article Availability
              </span>
              <h3 style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 800, color: C.g900 }}>
                Total Active & Inactive
              </h3>
            </div>
            <div style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: '#ECFDF5',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircle2 size={20} />
            </div>
          </div>

          {/* Dual Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'flex-start' }}>
            {/* Total Active Count */}
            <div style={{ borderRight: `1px solid ${C.g100}`, paddingRight: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.g500 }}>
                  Total Active
                </span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#059669',
                  background: '#ECFDF5',
                  padding: '2px 6px',
                  borderRadius: 10
                }}>
                  Live
                </span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#059669', marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {stats.active?.toLocaleString() ?? 0}
              </div>
              <span style={{ fontSize: 11, color: C.g400, marginTop: 4, display: 'block' }}>
                Published & available in app
              </span>
            </div>

            {/* Total Inactive Count */}
            <div style={{ paddingLeft: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.g500 }}>
                  Total Inactive
                </span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.g600,
                  background: C.g100,
                  padding: '2px 6px',
                  borderRadius: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3
                }}>
                  <EyeOff size={10} /> Inactive
                </span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.g700, marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {stats.inactive?.toLocaleString() ?? 0}
              </div>
              <span style={{ fontSize: 11, color: C.g400, marginTop: 4, display: 'block' }}>
                Draft & review status
              </span>
            </div>
          </div>

          {/* Subtitle / Health Note */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 12,
            borderTop: `1px solid ${C.g100}`,
            marginTop: 14
          }}>
            <span style={{ fontSize: 12, color: C.g600, fontWeight: 500 }}>
              {stats.total > 0
                ? `${Math.round(((stats.active || 0) / stats.total) * 100)}% articles active and visible`
                : 'Status synchronization healthy'}
            </span>
            <span style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#059669',
              background: '#ECFDF5',
              padding: '2px 8px',
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4
            }}>
              ● Live Feed
            </span>
          </div>
        </div>
      </div>

      {/* ─── DataTable with Category Filter Dropdown & QuestionList Pagination ─── */}
      <DataTable
        columns={columns}
        data={data}
        page={page}
        totalPages={totalPages}
        total={total}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={(newLimit) => {
          setItemsPerPage(newLimit);
          setPage(1);
        }}
        onPageChange={setPage}
        searchPlaceholder="Search articles..."
        filterOptions={['All', ...NEWS_STATUSES.map(formatLabel)]}
        onSearch={s => {
          setFilter(p => ({ ...p, search: s }));
          setPage(1);
        }}
        onFilter={f => {
          setFilter(p => f === 'All' ? { ...p, status: undefined } : { ...p, status: f.toLowerCase() });
          setPage(1);
        }}
        extraFilters={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
            <div style={{ position: 'relative', width: '100%' }}>
              <select
                value={selectedCategory}
                onChange={e => {
                  setSelectedCategory(e.target.value);
                  setPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1.5px solid ${selectedCategory ? C.primary : C.g200}`,
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  color: selectedCategory ? C.primary : C.g700,
                  background: selectedCategory ? '#EEF2FF' : '#fff',
                  outline: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  transition: 'all 0.15s ease'
                }}
              >
                <option value="">All Categories ({categories.length})</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedCategory && (
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory('');
                  setPage(1);
                }}
                title="Clear category filter"
                style={{
                  background: '#F1F5F9',
                  border: `1px solid ${C.g200}`,
                  borderRadius: 8,
                  padding: '9px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.g600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Clear
              </button>
            )}
          </div>
        }
        onEdit={openEdit}
        onDelete={handleDelete}
        expandRow={row => (
          <div style={{ display: 'flex', gap: 16 }}>
            <Thumbnail src={newsImage(row)} alt={row.title} width={140} height={90} />
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, color: C.g600, lineHeight: 1.6 }}>{row.description || 'No description available.'}</p>
              {row.keywords && <p style={{ margin: '8px 0 0', fontSize: 12, color: C.g400 }}>Keywords: {row.keywords}</p>}
              {row.link && <a href={row.link} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 13, color: C.primary, fontWeight: 600, textDecoration: 'none' }}>Read original →</a>}
            </div>
          </div>
        )}
      />

      {modal && (
        <Modal title={modal.id ? 'Edit Article' : 'Add Article'} onClose={() => setModal(null)}>
          <FormField label="Title" name="title" value={form.title} onChange={handleChange} required />
          <FormField label="Category" name="category_id" value={form.category_id} onChange={handleChange} options={categoryOptions} placeholder="Select category..." />
          <FormField label="Description / Summary" name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Short summary (shown as the card subtitle)..." />
          <FormField label="Body" name="body" value={form.body} onChange={handleChange} rows={8} placeholder="Full article body (shown in the mobile app)..." />
          <FormField label="Status" name="status" value={form.status} onChange={handleChange} options={NEWS_STATUSES} />
          {form._image && !files.length && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.g700 }}>Current Cover</label>
              <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
                <Thumbnail src={form._image} alt={form.title} width={160} height={100} radius={10} />
                {modal?.id && (
                  <button
                    type="button"
                    onClick={handleRemoveCover}
                    disabled={loading}
                    title="Remove image"
                    aria-label="Remove image"
                    style={{
                      position: 'absolute', top: -8, right: -8, width: 26, height: 26,
                      borderRadius: '50%', border: 'none', cursor: loading ? 'default' : 'pointer',
                      background: C.danger, color: '#fff', fontSize: 15, lineHeight: '26px',
                      padding: 0, boxShadow: '0 1px 4px rgba(0,0,0,.3)', opacity: loading ? 0.6 : 1,
                    }}
                  >
                    &times;
                  </button>
                )}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: C.g400 }}>
                Uploading a new image replaces it, or use &times; to remove it.
              </p>
            </div>
          )}
          <FileUpload label="Cover Image" files={files} onChange={f => setFiles(Array.from(f))} onRemove={() => setFiles([])} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : 'Save'}</PrimaryButton>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
};

export default NewsPage;
